import "./timelineScrollBackground.scss";

import { List } from "immutable";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withXScrollPositionContext } from "../../components/scrollable/ScrollPositionContext";
import {
  CALENDAR_MARKER_MIN_BAR_WIDTH,
  MIN_CALENDAR_MARKER_PIXEL_SPACING,
  TIMELINE_HEADER_VIEW_OVERSCROLL
} from "../../constants";
import { appStateSelectors } from "../../modules/appState";
import { calendarMarkerSelectors } from "../../modules/calendarMarkers";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import dateFunctions from "../../modules/definition/calendar/dateFunctions";
import { isOngoingAllowedForCalendar } from "../../modules/definition/calendar/selectors";
import { immutableRecords, PropTypeShapes } from "../../types";
import selectors from "./selectors";
import timelineViewUtils from "./utils";

const renderTodayLine = (nowTs, unitScale, virtualOffset) => {
  const absLeft = nowTs / unitScale;
  return (
    <div className="timelineBackgroundTodayLine" style={{ transform: `translateX(${absLeft - virtualOffset}px)` }} />
  );
};

class TimelineScrollBackgroundView extends PureComponent {
  constructor(props, context) {
    super(props, context);
  }

  getVisibleLabels(absoluteLeft, absoluteRight, unitScale) {
    const scrollLeft = absoluteLeft - TIMELINE_HEADER_VIEW_OVERSCROLL;
    const scrollRight = absoluteRight + TIMELINE_HEADER_VIEW_OVERSCROLL;

    return selectors.getTimelineHeaderLabels(
      this.props.calendar,
      this.props.viewIds.get(0),
      unitScale,
      scrollLeft,
      scrollRight,
      this.props.fontSize,
      this.props.dateRangeFrom,
      this.props.dateRangeTo
    );
  }

  getVisibleMarkers(scrollLeft, scrollRight, unitScale) {
    const leftTs = scrollLeft * unitScale;
    const rightTs = scrollRight * unitScale;
    const minRepeatSeconds = MIN_CALENDAR_MARKER_PIXEL_SPACING * unitScale;
    return selectors
      .getTimelineViewDisplayCalendarMarkers(
        this.props.calendarMarkers,
        this.props.calendar,
        leftTs,
        rightTs,
        minRepeatSeconds
      )
      .filter(m => m.drawOnTimeline);
  }

  showTodayLine(nowTs, scrollLeft, scrollRight, unitScale) {
    if (this.props.showTodayLine) {
      const leftTs = scrollLeft * unitScale;
      const rightTs = scrollRight * unitScale;
      return nowTs > leftTs && nowTs < rightTs;
    }
    return false;
  }

  renderHover() {
    if (!this.props.dragHoverDate) {
      return null;
    }

    const virtualOffset = this.props.xScrollPosition.canvas.min;
    const unitScale = this.props.xScrollPosition.zoom;

    const dragHoverX = Math.round(this.props.dragHoverDate.timestamp / unitScale) - virtualOffset;

    const lineStyle = {
      transform: `translateX(${dragHoverX}px)`
    };
    return <div className="timelineViewDragHoverLine" style={lineStyle} />;
  }

  render() {
    // Calculate left and right bounds
    const viewport = this.props.xScrollPosition.viewport;
    const virtualOffset = this.props.xScrollPosition.canvas.min;
    const unitScale = this.props.xScrollPosition.zoom;
    const labels = unitScale
      ? this.getVisibleLabels(viewport.min, viewport.max, unitScale)
      : immutableRecords.TimelineHeaderDisplayTicksRecord();

    const displayMarkers = unitScale ? this.getVisibleMarkers(viewport.min, viewport.max, unitScale) : List([]);

    const nowTs = dateFunctions.timestampForNow(this.props.calendar);
    const todayLine =
      isOngoingAllowedForCalendar(this.props.calendar) &&
      this.showTodayLine(nowTs, viewport.min, viewport.max, unitScale)
        ? renderTodayLine(nowTs, unitScale, virtualOffset)
        : null;

    return (
      <div className="timelineScrollBackgroundView">
        <div className="timelineScrollBackgroundCanvas">
          {todayLine}
          {labels.minor.map(t => {
            return (
              <div
                key={timelineViewUtils.getTickKey(t)}
                className="timelineBackgroundLine"
                style={{
                  transform: `translateX(${t.pixel - virtualOffset}px)`
                }}
              />
            );
          })}
          {displayMarkers.map(marker => {
            const m = marker.toJS();
            let absLeft = m.startDate.timestamp / unitScale;
            const absRight = m.endDate.timestamp / unitScale;
            let width = absRight - absLeft;
            const solidLine = width < CALENDAR_MARKER_MIN_BAR_WIDTH;
            if (solidLine) {
              width = 2;
              absLeft -= 1;
            }
            return (
              <div
                key={`${m.id}-${absLeft}`}
                className={`timelineBackgroundMarker ${m.color} ${solidLine ? "solidLine" : ""} ${
                  m.icon ? "hasIcon" : ""
                }`}
                style={{
                  transform: `translateX(${absLeft - virtualOffset}px)`,
                  width: `${width}px`
                }}
              />
            );
          })}
          {this.renderHover()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, _props) => {
  return {
    calendar: calendarSelectors.getCalendar(state.present),
    calendarMarkers: calendarMarkerSelectors.getCalendarMarkersList(state.present),
    fontSize: appStateSelectors.getFontSize(state.present),
    showTodayLine: definitionSelectors.getTimelineViewSettings(state.present).showTodayLine,
    dateRangeFrom: selectors.getActualDateFrom(state.present),
    dateRangeTo: selectors.getActualDateTo(state.present)
  };
};

const mapDispatchToProps = _dispatch => {
  return {};
};

TimelineScrollBackgroundView.propTypes = {
  fontSize: PropTypes.number.isRequired,

  showTodayLine: PropTypes.bool.isRequired,

  dateRangeFrom: PropTypeShapes.date,
  dateRangeTo: PropTypeShapes.date,

  calendar: PropTypeShapes.calendar.isRequired,
  calendarMarkers: PropTypeShapes.calendarMarkerList.isRequired,
  viewIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  // Shown when dragging items...
  dragHoverDate: PropTypeShapes.date,
  dragHoverScreenYPixel: PropTypes.number,

  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  xScrollActions: PropTypeShapes.scrollActions.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(withXScrollPositionContext(TimelineScrollBackgroundView));
